import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { makeStyles, tokens, Tooltip } from "@fluentui/react-components";
import { LinkDismissRegular, LinkEditRegular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./button";
import { ComboBox } from "./comboBox";
import { Link } from "./link";
import { useImpulse } from "../hooks/transientStateHooks";
const useStyles = makeStyles({
    linkDiv: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
        overflow: "hidden",
    },
    link: {
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
});
/**
 * A generic primitive component with a ComboBox for selecting from a list of entities.
 * Supports entities with duplicate names by using uniqueId for identity.
 * @param props ChooseEntityProps
 * @returns EntitySelector component
 */
export function EntitySelector(props) {
    const { value, onLink, getEntities, getName, filter, defaultValue } = props;
    const onChange = props.onChange;
    const classes = useStyles();
    const comboBoxRef = useRef(null);
    // Build options with uniqueId as key
    const options = useMemo(() => {
        return getEntities()
            .filter((e) => e.uniqueId !== undefined && (!filter || filter(e)))
            .map((entity) => ({
            label: getName(entity)?.toString() || "",
            value: entity.uniqueId.toString(),
        }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [getEntities, getName, filter]);
    const [isEditing, setIsEditing] = useState(false);
    const [enteringEditMode, pulseEnteringEditMode] = useImpulse();
    useEffect(() => {
        if (enteringEditMode) {
            comboBoxRef.current?.focus();
        }
    }, [enteringEditMode]);
    const handleEntitySelect = (key) => {
        const entity = getEntities().find((e) => e.uniqueId.toString() === key);
        onChange?.(entity ?? null);
        setIsEditing(false);
    };
    // Get current entity key for display
    const currentKey = value ? value.uniqueId.toString() : "";
    if (value && !isEditing) {
        // If there is a value and we are not editing, show the link view
        return (_jsxs("div", { className: classes.linkDiv, children: [_jsx(Tooltip, { content: getName(value), relationship: "label", children: _jsx(Link, { className: classes.link, value: getName(value), onLink: () => onLink(value) }) }), onChange &&
                    (defaultValue !== undefined ? (_jsx(Tooltip, { content: "Unlink", relationship: "label", children: _jsx(Button, { icon: LinkDismissRegular, onClick: () => {
                                pulseEnteringEditMode(true);
                                onChange(defaultValue);
                            } }) })) : (_jsx(Tooltip, { content: "Edit Link", relationship: "label", children: _jsx(Button, { icon: LinkEditRegular, onClick: () => {
                                pulseEnteringEditMode(true);
                                setIsEditing(true);
                            } }) })))] }));
    }
    else {
        // Otherwise, show the ComboBox for selection
        return _jsx(ComboBox, { ref: comboBoxRef, defaultOpen: enteringEditMode, label: "", options: options, value: currentKey, onChange: handleEntitySelect });
    }
}
EntitySelector.displayName = "EntitySelector";
//# sourceMappingURL=entitySelector.js.map